/**
 * React Query hook for organizations list
 *
 * Provides data fetching for organizations with pagination and filtering.
 */

import { useQuery } from '@tanstack/react-query';
import { OrganizationApiService } from '../services/organizationApi';
import type { OrganizationWithCounts, OrganizationStatus } from '../types/organization';

/**
 * Query key factory for organizations
 */
export const organizationsKeys = {
  all: ['organizations'] as const,
  list: (params: UseOrganizationsParams) =>
    ['organizations', 'list', params] as const,
};

/**
 * Parameters for useOrganizations hook
 */
export interface UseOrganizationsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: OrganizationStatus;
}

/**
 * Hook return type for useOrganizations
 */
export interface UseOrganizationsResult {
  organizations: OrganizationWithCounts[];
  totalPages: number;
  totalOrganizations: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * React Query hook for fetching organizations list
 *
 * @param params - Pagination and filter parameters
 * @returns Organizations data, pagination info, loading states, and refetch function
 *
 * @example
 * ```tsx
 * const { organizations, totalPages, isLoading, refetch } = useOrganizations({
 *   page: 1,
 *   pageSize: 20,
 *   search: 'acme',
 *   status: 'active',
 * });
 * ```
 */
export function useOrganizations(params: UseOrganizationsParams): UseOrganizationsResult {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: organizationsKeys.list(params),
    queryFn: async () => {
      const response = await OrganizationApiService.listOrganizations({
        page: params.page,
        page_size: params.pageSize,
        search: params.search,
        status: params.status,
      });
      return response;
    },
  });

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    organizations: data?.items ?? [],
    totalPages: data?.total_pages ?? 0,
    totalOrganizations: data?.total ?? 0,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
